/**
 * Single source of truth for Radix tooltip hover-open delays, in milliseconds.
 *
 * Consumed by every web surface that opens a tooltip: `libs/ui`'s own sidebar rail,
 * `libs/web-common`'s `BaseTooltip` (whose default this is) and shared components,
 * `apps/heartplus` (including its app-root `TooltipProvider`) and `apps/heartplus-admin`.
 *
 * Lives in `libs/ui` because it is the lowest lib every consumer depends on. The two
 * alternatives are both closed:
 * - `libs/web-common` cannot host it — `libs/ui` may not import `web-common`
 *   (`import/no-cycle`), and `apps/messaging-mfe` has no dependency on `web-common` at all.
 * - the `@heartplus/ui` barrel cannot expose it — `apps/heartplus/src/providers/app-provider.tsx`
 *   is the one call site that needs the browsing default, and importing the barrel there is a
 *   documented TDZ/circular-import crash. Deep-import `@heartplus/ui/tokens/tooltip-delays`
 *   only, exactly like the other modules in this folder.
 *
 * Why a value drifts if it is not here: a tooltip's delay is invisible at the call site, so a
 * component that simply omits it silently inherits whatever provider happens to be above it.
 * That is how HAR-3120's archived badge ended up at 700ms after a refactor dropped one prop,
 * and how six different delays (0/100/150/200/300/700) accumulated across ~67 call sites.
 *
 * Two tiers. They replaced six drifted values (0/100/150/200/300/700): `relaxed` is now
 * the default at BOTH ends (this token feeds `BaseTooltip`'s prop default and the
 * app-root provider), so a tooltip that specifies nothing behaves the same whichever
 * path it takes. `none` survives because 0 is not "a bit faster" — it is the only
 * correct value where the tooltip IS the label, as on the collapsed icon-only sidebar.
 *
 * Adding a third tier needs a reason a reader can name, not just a call site that felt
 * different. 100 and 700 were both retired for exactly that lack.
 */
export const TOOLTIP_DELAY_MS = {
  /** No delay — hover IS the interaction (dense grids, the collapsed sidebar rail). */
  none: 0,
  /**
   * The default everywhere. Slow enough not to fire while the eye travels, fast enough
   * to feel deliberate. Callers do not pass this — they get it by saying nothing.
   */
  relaxed: 200,
} as const;

export type TooltipDelayMs =
  (typeof TOOLTIP_DELAY_MS)[keyof typeof TOOLTIP_DELAY_MS];
